import { MarkerClusterer } from "@googlemaps/markerclusterer";
import _ from "lodash";

const defaultStyle = [
  {
    featureType: "poi",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit",
    stylers: [{ visibility: "off" }],
  },
];

let currentInfoWindow;
let currentBouncingMarker;

export const GoogleMapHook = {
  mounted() {
    this.markerPayload = {
      markers: JSON.parse(this.el.dataset.markers || "{}"),
      marker_radius: JSON.parse(this.el.dataset.markerRadius || "{}"),
      marker_clusters: JSON.parse(this.el.dataset.markerClusters || "{}"),
    };
    this.handleEvent(`${this.el.id}-markers`, (payload) => {
      this.markerPayload = payload;
      if (this.map) renderMap(this);
    });
    this.deferInit();
  },
  updated() {
    if (this.map) renderMap(this);
  },
  destroyed() {
    clearTimeout(this.initTimer);
    if (this.map) {
      removeStaleObjectsFromMap(this.allMarkers, this.allCircles, {});
      google.maps.event.clearInstanceListeners(this.map);
    }
  },
  // wait until google map is fully available
  // googlemap script is loaded async in the storybook
  deferInit(delay = 50) {
    if (typeof google === "object" && typeof google.maps === "object") {
      renderMap(this);
    } else {
      this.initTimer = setTimeout(() => this.deferInit(Math.min(delay * 2, 1000)), delay);
    }
  },
};

function openInfoWindow(map, marker, window) {
  if (currentInfoWindow) {
    currentInfoWindow.close();
  }
  currentInfoWindow = window;
  window.open(map, marker);
}

function parseMarkerOptions(dataset) {
  // We may group marker options in data-marker={color, template, events} over data-markerTemplate, data-markerColors, ...
  const { markerColors, markerEvents, markerTemplate } = dataset;
  const colors = JSON.parse(markerColors || "{}");
  const events = JSON.parse(markerEvents || "{}");
  const template = markerTemplate ? _.template(markerTemplate) : null;
  return { colors, events, template };
}

function renderMap(hook) {
  const { el } = hook;
  const dataset = el.dataset;
  const options = JSON.parse(dataset.options || "{}");
  const markerOptions = parseMarkerOptions(dataset);
  const fixedBounds = JSON.parse(dataset.bounds || "[]");
  const mapStyle = JSON.parse(dataset.mapStyle || "[]");
  const defaultMarkerColor = "#EA4335";
  const clusterColor = dataset.clusterColor;
  const clickMarkerEvent = dataset.clickMarkerEvent;

  const initializing = !hook.map;
  hook.map ||= new google.maps.Map(el, options);
  const map = hook.map;
  map.setOptions(options);
  removeStaleObjectsFromMap(hook.allMarkers || {}, hook.allCircles || {}, {});
  setMapStyle(map, defaultStyle.concat(mapStyle));

  // type is {name: {id: marker}}, where name: collection name, id: marker id
  hook.allMarkers = {};
  const allMarkers = hook.allMarkers;
  // type is {name: {id: circle}}, where name: collection name, id: poi id
  hook.allCircles = {};
  const allCircles = hook.allCircles;

  const { colors } = markerOptions;
  const icons = Object.entries(colors).reduce((acc, [key, color]) => {
    acc[key] = {
      path: "M24,23C21.257,23 19,20.743 19,18C19,15.257 21.257,13 24,13C26.743,13 29,15.257 29,18C29,20.743 26.743,23 24,23M24,4C16.32,4 10,10.32 10,18C10,28.5 24,44 24,44C24,44 38,28.5 38,18C38,10.32 31.68,4 24,4",
      anchor: new google.maps.Point(24, 40),
      fillOpacity: 1,
      strokeWeight: 0,
      fillColor: `${color}`,
    };
    return acc;
  }, {});

  const {
    markers,
    marker_radius: markerRadius,
    marker_clusters: markerClusters,
  } = hook.markerPayload;

  // incoming type is {name: marker[]}, where name is the collection name, marker is {lat, lng, id}.
  // Build bounds for those markers
  // An approach is to start with empty bounds and add markers.
  // See https://developers.google.com/maps/documentation/javascript/reference/coordinates?hl=en#LatLngBounds
  //
  // But we can not remove markers from bounds...
  // see https://stackoverflow.com/questions/10268033/google-maps-api-v3-method-fitbounds
  const [bounds, addMarkerToBounds] =
    fixedBounds.length === 0
      ? [new google.maps.LatLngBounds(), true]
      : [buildBounds(fixedBounds), false];

  for (const name of Object.keys(markers)) {
    const color = markerOptions.colors[name] || defaultMarkerColor;
    if (!allMarkers[name]) {
      allMarkers[name] = {};
    }
    if (!allCircles[name]) {
      allCircles[name] = {};
    }
    drawMarkers(
      map,
      name,
      allMarkers[name],
      markers[name],
      addMarkerToBounds ? bounds : undefined,
      color,
      clusterColor,
      icons[name],
      markerOptions,
      markerRadius[name],
      allCircles[name],
      markerClusters[name],
      clickMarkerEvent,
      hook,
    );
  }
  if (!bounds.isEmpty()) map.fitBounds(bounds);

  if (!initializing) return;

  hook.handleEvent(`${el.id}-marker-unfocus`, (_data) => {
    if (currentBouncingMarker) {
      currentBouncingMarker.marker.setAnimation(null);
      currentBouncingMarker = null;
    }
  });

  hook.handleEvent(`${el.id}-marker-focus`, ({ id: markerId }) => {
    _.each(hook.allMarkers, (collection) => {
      const marker = collection[markerId];

      if (currentBouncingMarker && currentBouncingMarker.id !== markerId) {
        currentBouncingMarker.marker.setAnimation(null);
      }

      if (marker) {
        currentBouncingMarker = { marker: marker, id: markerId };
        marker.setAnimation(google.maps.Animation.BOUNCE);
      }
    });
  });

  // close info window on outside map click
  google.maps.event.addListener(map, "click", () => {
    if (currentInfoWindow) {
      currentInfoWindow.close();
    }
  });
}

function drawMarkers(
  map,
  name,
  allMarkers,
  markerInfos,
  bounds,
  color,
  clusterColor,
  icon,
  markerOptions,
  markerRadius,
  allCircles,
  markerClustersEnabled,
  clickMarkerEvent,
  hook,
) {
  const { shadow } = hook;
  const markers = [];
  const { events, template } = markerOptions;

  for (const markerInfo of markerInfos) {
    const { id, lat, lng } = markerInfo;
    const position = { lat, lng };

    if (!allMarkers[id]) {
      const marker = markerClustersEnabled
        ? new google.maps.Marker({ icon, position, shadow })
        : new google.maps.Marker({ icon, map, position, shadow });

      if (template) {
        const infoWindow = new google.maps.InfoWindow({
          content: template(markerInfo),
          headerDisabled: true,
        });
        marker.addListener("click", () => openInfoWindow(map, marker, infoWindow));
      }

      if (clickMarkerEvent === "true") {
        marker.addListener("click", () =>
          hook.pushEvent("click_marker_event", { shop_id: markerInfo.shop_id }),
        );
      }

      attachEventListeners(marker, markerInfo, events[name], hook);
      allMarkers[id] = marker;
    }

    markers.push(allMarkers[id]);

    if (markerRadius) {
      let circle = allCircles[id];
      if (!circle) {
        circle = new google.maps.Circle({
          map,
          center: position,
          fillColor: color,
          fillOpacity: 0.3,
          strokeOpacity: 0,
          radius: markerRadius,
        });
        allCircles[id] = circle;
      } else if (circle.radius !== markerRadius) {
        circle.setRadius(markerRadius);
      }
    }

    if (bounds) {
      bounds.extend(position);
    }
  }

  if (markerClustersEnabled) {
    if (allMarkers.cluster) {
      allMarkers.cluster.delete();
    }

    allMarkers.cluster = new MarkerClusterer({
      markers,
      map,
      renderer: clusterRenderer(clusterColor),
    });
  }
}

function attachEventListeners(marker, info, events, hook) {
  if (!events) return;
  for (const e of events) {
    const { on: event, push } = e;
    const { event: name, payload: builder } = push;
    const payload = buildEventPayload(builder, info);
    marker.addListener(event, () => hook.pushEvent(name, payload));
  }
}

function buildEventPayload(builder, info) {
  if (!builder) return {};
  return Object.entries(builder).reduce((acc, [k, { marker: attribute }]) => {
    acc[k] = info[attribute];
    return acc;
  }, {});
}

function buildBounds([{ lat: swLat, lng: swLng }, { lat: neLat, lng: neLng }]) {
  const bounds = new google.maps.LatLngBounds(
    new google.maps.LatLng(swLat, swLng),
    new google.maps.LatLng(neLat, neLng),
  );
  return bounds;
}

// allMarkers: {name: {id: marker}} where marker is google.maps.Marker
// allRadiuses: {name: {id: circle}} where circle is google.maps.Circle
// markers: {name: [{id, lat, lng}]} markers by collection name
function removeStaleObjectsFromMap(allMarkers, allRadiuses, markers) {
  // Here I turn {key: [{id}]} to {key: set<id>}
  const sets = Object.keys(markers).reduce((acc, k) => {
    acc[k] = new Set(markers[k].map(({ id }) => id));
    return acc;
  }, {});
  removeFromMapWhereNotInSet(allMarkers, sets);
  removeFromMapWhereNotInSet(allRadiuses, sets);
}

// This is a nested set difference, but types are not sets :)
// previous : {name: {id: circle|marker}}
// sets: {name: set<id>}
function removeFromMapWhereNotInSet(previous, sets) {
  for (const key of Object.keys(previous)) {
    const previousCollection = previous[key];
    const set = sets[key];
    if (set) {
      for (const id of Object.keys(previousCollection)) {
        if (!set.has(id)) {
          removeStaleObjectFromContainerAndGoogleMap(previousCollection, id);
        }
      }
    } else {
      for (const id of Object.keys(previousCollection)) {
        removeStaleObjectFromContainerAndGoogleMap(previousCollection, id);
      }
      delete previous[key];
    }
  }
}

function removeStaleObjectFromContainerAndGoogleMap(collection, id) {
  collection[id].setMap(null);
  delete collection[id];
}

function setMapStyle(map, mapStyle) {
  const styledMapType = new google.maps.StyledMapType(mapStyle, {
    name: "Styled Map",
  });
  map.mapTypes.set("styled_map", styledMapType);
  map.setMapTypeId("styled_map");
}

const clusterRenderer = (clusterColor) => {
  return {
    render: (cluster) => {
      // change color if this cluster has more markers than the mean cluster
      const color = clusterColor;

      // create svg url with fill color
      const svg = window.btoa(`
      <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
        <circle cx="120" cy="120" opacity=".9" r="70" />
        <circle cx="120" cy="120" opacity=".6" r="90" />
        <circle cx="120" cy="120" opacity=".4" r="110" />
      </svg>`);

      // create marker using svg icon
      return new google.maps.Marker({
        position: cluster.position,
        icon: {
          url: `data:image/svg+xml;base64,${svg}`,
          scaledSize: new google.maps.Size(45, 45),
        },
        label: {
          text: String(cluster.count),
          color: "rgba(255,255,255,0.9)",
          fontSize: "12px",
        },
        title: `Cluster of ${cluster.count} markers`,
        // adjust zIndex to be above other markers
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + cluster.count,
      });
    },
  };
};
